import { platform } from "./capacitor";
// import { toHex } from "./math";
import { notifyError } from "./quasar";
import pako from "pako";

export type ListenerItem = {
  listener: (event: Event) => void;
  serviceBluetoothServiceUUID: BluetoothServiceUUID;
  characteristicBluetoothServiceUUID: BluetoothServiceUUID;
};

export type BluetoothRawOption = {
  name: string;
  services: {
    name: string;
    uuid: BluetoothServiceUUID;
    characteristics?: {
      name: string;
      uuid: BluetoothServiceUUID;
      properties: string[];
    }[];
  }[];
};

export type Options = {
  bluetoothRawOptions?: BluetoothRawOption[];
};

export type ReadOptions = {
  showRawRes?: boolean; // 默认数据会 JSON 化, showRawRes 为 true 时不转换 JSON
  // notReconnect?: boolean; // 不自动从新连接
  notShowNotify?: boolean; // 不显示通知
  notCompress?: boolean; // 不压缩
};

// 是否支持蓝牙
export const supportBluetoothValidate = () => {
  if (platform === "web") {
    if ("bluetooth" in navigator) {
      console.log("Web Bluetooth API is supported!");
    } else {
      console.log("Web Bluetooth API is not supported.");
    }
    return "bluetooth" in navigator;
  }
  if (platform === "android" || platform === "ios") return true;
  return false;
};

export const isSupportBluetooth = supportBluetoothValidate();

// 压缩数据
export function compressBuffer(buffer: Uint8Array): Uint8Array {
  return new Uint8Array(pako.gzip(buffer));
}

// 解压数据
export function decompressBuffer(compressedBuffer: Uint8Array): Uint8Array {
  return new Uint8Array(pako.ungzip(compressedBuffer));
}

export const convertToBuffer = (
  input: string | number,
  options?: ReadOptions
): ArrayBuffer => {
  if (typeof input === "string") {
    const encoder = new TextEncoder();
    // const string = JSON.stringify({ TEST: 1111 })
    // console.log("[BOWEN_LOG] 🚀 ~~ convertToBuffer ~~ string:", string);
    let buffer = encoder.encode(input);
    // console.log("[BOWEN_LOG] 🚀 ~~ convertToBuffer ~~ 未压缩:", buffer);
    if (!options?.notCompress) {
      buffer = compressBuffer(buffer); // 压缩
    }
    // console.log("[BOWEN_LOG] 🚀 ~~ convertToBuffer ~~ 已压缩:", buffer);
    return buffer;
  }
  // else if (typeof input === "number") {
  //   const buffer = new ArrayBuffer(4); // 假设数字以 4 字节存储
  //   const view = new DataView(buffer);
  //   view.setFloat32(0, input); // 或者使用 setInt32 根据具体需求
  //   return buffer;
  // }
  else {
    throw new TypeError("[convertToBuffer] Input must be a string or a number");
  }
};
export const convertToString = (value: DataView): string => {
  // console.log("[BOWEN_LOG] 🚀 ~~ convertToString ~~ value:", value);
  if (value instanceof DataView) {
    let uint8Array = new Uint8Array(value.buffer);
    // console.log("[BOWEN_LOG] 🚀 ~~ convertToString ~~ 未解压:", uint8Array);
    // uint8Array = decompressBuffer(uint8Array);
    // console.log("[BOWEN_LOG] 🚀 ~~ convertToString ~~ 已解压:", uint8Array);
    const textDecoder = new TextDecoder("utf-8");
    const stringValue = textDecoder.decode(uint8Array);
    return stringValue;
  } else {
    throw new TypeError("[convertToString] value must be a DataView");
  }
};

export const JSONParse = (data: string) => {
  try {
    const res = JSON.parse(data);
    return res;
  } catch (error) {
    return data;
  }
};

export const getAllServicesBluetoothServiceUUID = (
  bluetoothRawOptions: BluetoothRawOption[]
) => {
  const list = new Set<BluetoothServiceUUID>();
  bluetoothRawOptions.map((item) => {
    const { services } = item;
    services.map((item) => list.add(item.uuid));
  });
  const uuids = [...list];
  return uuids;
};

// type BluetoothServiceUUID = number | string | any;

export const notifyErrorBluetooth = (error: Error, tag: string = "LOG") => {
  notifyError(error, `Bluetooth ${tag}`);
};

export class BluetoothProxy {
  public isConnected = false;
  public device = null as any as BluetoothDevice; // 蓝牙设备实例
  public bluetoothRawOptions = null as any as BluetoothRawOption[]; // 蓝牙配置
  private queue: Array<() => Promise<void>> = [];
  private isProcessing = false;
  private notificationListeners = [] as ListenerItem[]; // 所有订阅的通知

  constructor({ bluetoothRawOptions = [] }: Options) {
    this.onDisconnected = this.onDisconnected.bind(this);
    this.bluetoothRawOptions = bluetoothRawOptions;
  }

  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;
    this.isProcessing = true;
    const task = this.queue.shift();
    if (task) {
      try {
        await task();
      } catch (error) {
        notifyErrorBluetooth(error as Error, "processQueue");
      }
      this.isProcessing = false;
      this.processQueue();
    }
  }

  // request({ bluetoothRawOptions }: Options = {}) {
  //   this.bluetoothRawOptions = bluetoothRawOptions || this.bluetoothRawOptions;
  //   let options = {
  //     filters: this.bluetoothRawOptions.map((item) => ({ name: item.name })),
  //     optionalServices: getAllServicesBluetoothServiceUUID(
  //       this.bluetoothRawOptions
  //     ),
  //   };
  //   return navigator.bluetooth
  //     .requestDevice(options)
  //     .then((device) => {
  //       this.device = device;
  //       this.device.addEventListener(
  //         "gattserverdisconnected",
  //         this.onDisconnected
  //       );
  //       return device;
  //     })
  //     .catch((error) => {
  //       if (error.message.includes("User cancelled")) {
  //         throw error; // 用户取消
  //       }
  //       notifyErrorBluetooth(error, "requestDevice");
  //       throw error;
  //     });
  // }
  // 当 getDevices 可以用时，使用 getDevices 获取设备 否则使用 requestDevice 获取设备
  request({ bluetoothRawOptions }: Options = {}) {
    this.bluetoothRawOptions = bluetoothRawOptions || this.bluetoothRawOptions;
    let options = {
      filters: this.bluetoothRawOptions.map((item) => ({ name: item.name })),
      optionalServices: getAllServicesBluetoothServiceUUID(
        this.bluetoothRawOptions
      ),
    };
    if (navigator.bluetooth.getDevices) {
      return navigator.bluetooth
        .getDevices()
        .then((devices) => {
          console.log(
            "[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ request ~~ devices:",
            devices
          );
          devices.forEach((device) => {
            console.log(
              "[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ request ~~ device:",
              device
            );
            if (device.name === options.filters[0].name) {
              this.device = device;
              this.device.addEventListener(
                "gattserverdisconnected",
                this.onDisconnected
              );
              return device;
            }
          });
        })
        .catch((error) => {
          if (error.message.includes("User cancelled")) {
            throw error; // 用户取消
          }
          notifyErrorBluetooth(error, "requestDevice");
          throw error;
        });
    } else {
      return navigator.bluetooth
        .requestDevice(options)
        .then((device) => {
          this.device = device;
          this.device.addEventListener(
            "gattserverdisconnected",
            this.onDisconnected
          );
          return device;
        })
        .catch((error) => {
          if (error.message.includes("User cancelled")) {
            throw error; // 用户取消
          }
          notifyErrorBluetooth(error, "requestDevice");
          throw error;
        });
    }
  }

  connect(onDisconnected = () => {}) {
    if (!this.device) {
      return Promise.reject("Device is not connected.");
    }
    this.device?.addEventListener?.("gattserverdisconnected", onDisconnected);
    return this.device?.gatt
      ?.connect()
      .then(() => (this.isConnected = true))
      .catch((error) => {
        notifyErrorBluetooth(error, "connect");
        throw error;
      });
  }

  // 获取所有特征
  getCharacteristics() {
    return this.device?.gatt
      ?.getPrimaryServices()
      .then((services) =>
        Promise.all(services.map((service) => service.getCharacteristics()))
      )
      .then((characteristics) => characteristics.flat());
  }

  // 获取所有服务
  getServices() {
    return this.device?.gatt?.getPrimaryServices();
  }

  readData(
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return (
      this.device?.gatt
        // ?.connect()
        // .then((server) => server?.getPrimaryService(serviceBluetoothServiceUUID))
        ?.getPrimaryService(serviceBluetoothServiceUUID)
        .then((service) =>
          service.getCharacteristic(characteristicBluetoothServiceUUID)
        )
        .then((characteristic) =>
          characteristic?.readValue().then((value) => {
            if (!value) return null;
            return options?.showRawRes
              ? convertToString(value)
              : JSONParse(convertToString(value)); // parse JSON
          })
        )
        .catch((error) => {
          notifyErrorBluetooth(error, "readData");
          throw error;
        })
    );
  }

  writeData(
    data: string | number | ArrayBuffer,
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    // if (this.device.gatt?.connected === false) {
    //   console.log("[writeData] 设备连接已断开", this.device?.gatt);
    //   // if (options?.notReconnect) {
    //   //   return;
    //   // }
    //   console.log("[writeData] 尝试重新连接", this.device?.gatt);
    //   this.device?.gatt?.connect().then(() => {
    //     console.log("[writeData] 重新连接:", this.device.gatt?.connected);
    //   });
    //   return;
    // }
    // to json string
    data = options?.showRawRes ? data : JSON.stringify(data);
    // to DataView
    const bufferData =
      typeof data === "string" || typeof data === "number"
        ? convertToBuffer(data, options)
        : data;
        // console.log("[BOWEN_LOG] 🚀 ~~ BluetoothPluginProxy ~~ dataview:", bufferData);
    return (
      this.device?.gatt
        // ?.connect()
        // .then((server) => server?.getPrimaryService(serviceBluetoothServiceUUID))
        ?.getPrimaryService(serviceBluetoothServiceUUID)
        .then((service) =>
          service.getCharacteristic(characteristicBluetoothServiceUUID)
        )
        .then((characteristic) =>
          characteristic.writeValueWithoutResponse(bufferData)
        )
        .catch((error) => {
          !options?.notShowNotify && notifyErrorBluetooth(error, "writeData");
          throw error;
        })
    );
  }
  // 顺序执行 写入数据
  async queuePerformWriteData(
    data: string | number | ArrayBuffer,
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return new Promise<any>((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const res = await this.writeData(
            data,
            serviceBluetoothServiceUUID,
            characteristicBluetoothServiceUUID,
            options
          );
          console.log("[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ writeData ~~ res:", res);
          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }
  // 顺序执行 读取数据
  async queuePerformReadData(
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return new Promise<any>((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const res = await this.readData(
            serviceBluetoothServiceUUID,
            characteristicBluetoothServiceUUID,
            options
          );
          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }

  startNotifications(
    listener: (value: any, event: Event) => void,
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    options?: ReadOptions
  ) {
    return (
      this.device?.gatt
        // ?.connect()
        // .then((server) => server?.getPrimaryService(serviceBluetoothServiceUUID))
        ?.getPrimaryService(serviceBluetoothServiceUUID)
        .then((service) =>
          service.getCharacteristic(characteristicBluetoothServiceUUID)
        )
        .then((characteristic) => characteristic.startNotifications())
        .then((characteristic) => {
          const curListener = (event: Event) => {
            const value = (event?.target as any)?.value;
            if (!value?.byteLength) return;
            // console.log("[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ curListener ~~ value:", value,event);
            listener(
              options?.showRawRes
                ? convertToString(value)
                : JSONParse(convertToString(value)),
              event
            );
          };
          characteristic.addEventListener(
            "characteristicvaluechanged",
            curListener
          );
          // record
          this.notificationListeners.push({
            serviceBluetoothServiceUUID,
            characteristicBluetoothServiceUUID,
            listener: curListener,
          });
          return characteristic;
        })
        .catch((error) => {
          !options?.notShowNotify && notifyErrorBluetooth(error, "startNotifications");
          throw error;
        })
    );
  }

  stopNotifications(
    serviceBluetoothServiceUUID: BluetoothServiceUUID,
    characteristicBluetoothServiceUUID: BluetoothServiceUUID,
    listener?: (event: Event) => void
  ) {
    return (
      this.device?.gatt
        // ?.connect()
        // .then((server) => server?.getPrimaryService(serviceBluetoothServiceUUID))
        ?.getPrimaryService(serviceBluetoothServiceUUID)
        .then((service) =>
          service.getCharacteristic(characteristicBluetoothServiceUUID)
        )
        .then((characteristic) => characteristic.stopNotifications())
        .then((characteristic) => {
          if (listener) {
            return characteristic.removeEventListener(
              "characteristicvaluechanged",
              listener
            );
          }
          // find corresponding listener
          const notifycation = this.notificationListeners.find(
            (item) =>
              item.serviceBluetoothServiceUUID ===
                serviceBluetoothServiceUUID &&
              item.characteristicBluetoothServiceUUID ===
                characteristicBluetoothServiceUUID
          );
          if (!notifycation) return false;
          return characteristic.removeEventListener(
            "characteristicvaluechanged",
            notifycation.listener
          );
        })
        .catch((error) => {
          notifyErrorBluetooth(error, "stopNotifications");
          throw error;
        })
    );
  }
  async stopAllNotifications() {
    const notificationListeners = [...this.notificationListeners];
    for (const item of notificationListeners) {
      try {
        await this.stopNotifications(
          item.serviceBluetoothServiceUUID,
          item.characteristicBluetoothServiceUUID,
          item.listener
        );
        const index = this.notificationListeners.findIndex(
          (notify) =>
            notify.characteristicBluetoothServiceUUID ===
            item.characteristicBluetoothServiceUUID
        );
        if (index !== -1) this.notificationListeners.splice(index, 1);
      } catch (error) {
        console.log(
          "[BOWEN_LOG] 🚀 ~~ BluetoothProxy ~~ stopAllNotifications ~~ error:",
          error
        );
      }
    }
    console.log("All notifications stopped.", this.notificationListeners);
  }

  async disconnect() {
    // remove EventListeners
    // await this.stopAllNotifications();
    if (!this.device) {
      return Promise.reject("Device is not connected.");
    }
    return this.device?.gatt?.disconnect();
  }

  onDisconnected() {
    this.isConnected = false;
    console.log("Device is disconnected.");
  }
}
